// Small motion helpers for lists that change under the person's eyes (the dashboard's
// Activity): rows that arrive fade in, rows that go fade and fold away, and the rest glide
// to their new places instead of jumping. Nothing moves with prefers-reduced-motion.

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';

export const reducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/** With Reduce Motion a surface cross-fades instead of moving: --dur-reduced (tokens.css), as the Mac's. */
export const REDUCED_MS = 150;

/** The sidebar hiding and showing (--dur-xl): its column and the sidebar move together for this long. */
export const SIDEBAR_MS = 280;
/** Panes the machine adds or takes away (an agent's split) come and go, and the others make room, for this long
 *  (--dur-xl, as the sidebar: page-wide surfaces). */
export const GLIDE_MS = 280;
/** While the sidebar moves or the panes glide, terminals hold their size; this event on window says it has settled. */
export const LAYOUT_SETTLED = 'cmx:layout-settled';
export const layoutMoving = () => document.querySelector('.app[data-sb-moving], .ws-panes[data-gliding]') !== null;

/** How long a leaving row takes to fade and fold away. */
export const LEAVE_MS = 200;
const EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const folding = new WeakSet<HTMLElement>();

export type Shown<T> = { item: T; key: string; phase: 'stay' | 'enter' | 'leave' };

/**
 * The rows to draw for `items`: each with its phase, rows that just went kept in their old
 * place for LEAVE_MS. Put the returned ref on the list and data-key={key} on each row; rows
 * that move between draws are animated from where they were (FLIP).
 */
export function useListMotion<T, E extends HTMLElement = HTMLElement>(items: T[], keyOf: (t: T) => string | number) {
  const ref = useRef<E>(null);
  const drawn = useRef<Shown<T>[] | null>(null);
  const gone = useRef(new Map<string, { item: T; index: number; until: number }>());
  const tops = useRef(new Map<string, number>());
  const [tick, setTick] = useState(0);

  const shown = useMemo(() => {
    const now = Date.now();
    const before = drawn.current;
    const keys = new Set(items.map((t) => String(keyOf(t))));
    const had = new Set(before?.filter((s) => s.phase !== 'leave').map((s) => s.key));
    if (before && !reducedMotion())
      before.forEach((s, index) => {
        if (s.phase !== 'leave' && !keys.has(s.key)) gone.current.set(s.key, { item: s.item, index, until: now + LEAVE_MS });
      });
    for (const [k, g] of gone.current) if (keys.has(k) || g.until <= now) gone.current.delete(k);
    const out: Shown<T>[] = items.map((item) => {
      const key = String(keyOf(item));
      return { item, key, phase: !before || had.has(key) ? 'stay' : 'enter' };
    });
    for (const [key, g] of gone.current) out.splice(Math.min(g.index, out.length), 0, { item: g.item, key, phase: 'leave' });
    return out;
    // keyOf is a plain accessor; tick redraws once leaving rows are done.
  }, [items, tick]);

  useLayoutEffect(() => {
    drawn.current = shown;
    const list = ref.current;
    if (list) {
      const reduce = reducedMotion();
      const next = new Map<string, number>();
      for (const row of list.querySelectorAll<HTMLElement>(':scope > [data-key]')) {
        const key = row.dataset.key!;
        if (row.classList.contains('is-leaving')) {
          // Fold from its own height (CSS can't animate from height: auto).
          if (!folding.has(row)) {
            folding.add(row);
            const h = `${row.offsetHeight}px`;
            row.animate([{ height: h, minHeight: h, opacity: 1 }, { height: '0px', minHeight: '0px', opacity: 0 }], { duration: LEAVE_MS, easing: EASE, fill: 'forwards' });
          }
          continue;
        }
        if (folding.delete(row)) row.getAnimations().forEach((a) => a.cancel()); // back before it was gone
        const top = row.offsetTop;
        next.set(key, top);
        const was = tops.current.get(key);
        if (!reduce && was !== undefined && was !== top) row.animate([{ transform: `translateY(${was - top}px)` }, { transform: 'none' }], { duration: 240, easing: EASE });
      }
      tops.current = next;
    }
    if (!shown.some((s) => s.phase === 'leave')) return;
    const t = setTimeout(() => setTick((n) => n + 1), LEAVE_MS);
    return () => clearTimeout(t);
  }, [shown]);

  return { ref: ref as RefObject<E>, shown };
}

/** Whether el is on screen and the page is in front: what a live view polls while. */
export function useOnScreen(ref: RefObject<Element | null>) {
  const [inView, setInView] = useState(false);
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible');
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, [ref]);
  useEffect(() => {
    const on = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);
  return inView && visible;
}
